import { useCallback, useEffect, useRef, useState } from "react"
import { coreGet } from "@/services/coreApi"
import { useRouteStore } from "@/features/route/state/RouteStore.jsx"
import { normalizeTreeMetadata } from "@/features/route/state/RouteAdapters.jsx"

// Loads GET /vessels into the RouteStore — the single source for vessel
// master data (tree, pickers, spec views, CII request). Mounted once in
// MainComponent: loads at login and again whenever `refreshSignal` turns
// truthy (a panel opens), so edits by others are picked up like before.
// Own changes (PATCH, import) are upserted into the store directly.
export default function useVesselsLoader({
  accessContext,
  setAccessContext,
  refreshSignal,
}) {
  const { setVessels } = useRouteStore()
  const loadedOnceRef = useRef(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  // Read auth through a ref so a token refresh does not refetch.
  const authRef = useRef({ accessContext, setAccessContext })
  authRef.current = { accessContext, setAccessContext }

  const reload = useCallback(async () => {
    const auth = authRef.current
    if (!auth.accessContext?.token) return
    setLoading(true)
    setError("")
    try {
      const data = await coreGet(
        "/vessels",
        auth.accessContext,
        auth.setAccessContext,
        ".core /vessels error=",
      )
      setVessels(normalizeTreeMetadata({ vessels: data?.vessels ?? [] }))
    } catch (err) {
      setError(err?.message ?? "Failed to load vessels")
    } finally {
      setLoading(false)
    }
  }, [setVessels])

  const hasToken = Boolean(accessContext?.token)
  useEffect(() => {
    if (!hasToken) return
    if (loadedOnceRef.current && !refreshSignal) return
    loadedOnceRef.current = true
    reload()
  }, [hasToken, refreshSignal, reload])

  return { loading, error }
}
